import { extent, scaleLog, scaleTime, ZoomTransform } from 'd3'
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Bound } from 'state/mint/v3/actions'
import styled, { useTheme } from 'styled-components/macro'

import { getChartPriceDomain } from './chartUtils'
import { Line } from './Line'
import { LiquidityBars } from './LiquidityBars'
import { PriceHistory, PriceHistoryAxis } from './PriceHistory'
import { LiquidityChartRangeInputProps } from './types'
import { VerticalBrush } from './VerticalBrush'
import Zoom from './Zoom'

const LIQUIDITY_RAIL_WIDTH = 104
const RANGE_INDICATOR_WIDTH = 16

const ChartHeader = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  min-height: 24px;
  padding: 0 2px 4px;
`

const HeaderPrice = styled.strong`
  color: ${({ theme }) => theme.textPrimary};
  font-size: 15px;
  font-variant-numeric: tabular-nums;
`

const HeaderMeta = styled.span`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
`

const HoverLine = styled.line`
  stroke: ${({ theme }) => theme.textSecondary};
  stroke-dasharray: 3 3;
  stroke-width: 1;
`

const HoverPoint = styled.circle`
  fill: ${({ theme }) => theme.backgroundSurface};
  stroke: ${({ theme }) => theme.textPrimary};
  stroke-width: 2;
`

const clamp = (value: number, minimum: number, maximum: number) => Math.max(minimum, Math.min(maximum, value))

export function Chart({
  id = 'liquidityChartRangeInput',
  data: { series, current, history },
  ticksAtLimit,
  styles,
  dimensions: { width, height },
  margins,
  interactive = true,
  brushLabels,
  brushDomain,
  onBrushDomainChange,
  zoomLevels,
}: LiquidityChartRangeInputProps) {
  const theme = useTheme()
  const zoomRef = useRef<SVGSVGElement | null>(null)

  const [zoom, setZoom] = useState<ZoomTransform | null>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const [innerHeight, innerWidth] = useMemo(
    () => [height - margins.top - margins.bottom, width - margins.left - margins.right],
    [width, height, margins]
  )
  const mainPlotWidth = Math.max(80, innerWidth - LIQUIDITY_RAIL_WIDTH)

  const { timeScale, yScale } = useMemo(() => {
    const timeDomain = extent(history, (point) => point.timestamp) as [Date | undefined, Date | undefined]
    const scales = {
      timeScale: scaleTime()
        .domain(
          timeDomain[0] && timeDomain[1] && timeDomain[0].getTime() !== timeDomain[1].getTime()
            ? [timeDomain[0], timeDomain[1]]
            : [new Date(Date.now() - 86_400_000), new Date()]
        )
        .range([12, Math.max(12, mainPlotWidth - 4)]),
      // Uniswap's range chart is linear in ticks, which is logarithmic in
      // price. This keeps wide ranges readable instead of flattening them.
      yScale: scaleLog()
        .domain(
          getChartPriceDomain({
            current,
            history,
            brushDomain: history.length > 1 ? undefined : brushDomain,
            zoomLevels,
          })
        )
        .range([innerHeight, 0]),
    }

    if (zoom) {
      const zoomedScale = zoom.rescaleY(scales.yScale)
      scales.yScale.domain(zoomedScale.domain())
    }

    return scales
  }, [brushDomain, current, history, innerHeight, mainPlotWidth, zoom, zoomLevels])

  const hoveredPoint = hoveredIndex === null ? history[history.length - 1] : history[hoveredIndex]
  const firstPoint = history[0]
  const priceChange =
    hoveredPoint && firstPoint && firstPoint.price > 0
      ? ((hoveredPoint.price - firstPoint.price) / firstPoint.price) * 100
      : undefined
  const resetKey = `${history[0]?.timestamp.getTime() ?? 0}-${history[history.length - 1]?.timestamp.getTime() ?? 0}`

  const [rangeMinimum, rangeMaximum] = useMemo<[number, number]>(() => {
    const domain = brushDomain ?? (yScale.domain() as [number, number])
    return domain[0] <= domain[1] ? domain : [domain[1], domain[0]]
  }, [brushDomain, yScale])
  const visibleRangeTop = clamp(yScale(rangeMaximum), 0, innerHeight)
  const visibleRangeBottom = clamp(yScale(rangeMinimum), 0, innerHeight)
  const visibleCurrentY = clamp(yScale(current), visibleRangeTop, visibleRangeBottom)
  const rangeWidth = innerWidth - RANGE_INDICATOR_WIDTH
  const upperRangeHeight = Math.max(0, visibleCurrentY - visibleRangeTop)
  const lowerRangeHeight = Math.max(0, visibleRangeBottom - visibleCurrentY)

  const onMouseMove = (event: MouseEvent<SVGSVGElement>) => {
    if (!history.length) return
    const bounds = event.currentTarget.getBoundingClientRect()
    const x = ((event.clientX - bounds.left) / bounds.width) * width - margins.left

    if (x < 0 || x > mainPlotWidth) {
      setHoveredIndex(null)
      return
    }

    const timestamp = timeScale.invert(clamp(x, 0, mainPlotWidth)).getTime()
    let closest = 0
    for (let index = 1; index < history.length; index++) {
      if (
        Math.abs(history[index].timestamp.getTime() - timestamp) <
        Math.abs(history[closest].timestamp.getTime() - timestamp)
      ) {
        closest = index
      }
    }
    setHoveredIndex(closest)
  }

  useEffect(() => {
    setZoom(null)
  }, [zoomLevels])

  useEffect(() => {
    if (!brushDomain) {
      onBrushDomainChange(yScale.domain() as [number, number], undefined)
    }
  }, [brushDomain, onBrushDomainChange, yScale])

  return (
    <>
      <Zoom
        svg={zoomRef.current}
        setZoom={setZoom}
        width={innerWidth}
        height={innerHeight}
        resetBrush={() => {
          onBrushDomainChange(
            [current * zoomLevels.initialMin, current * zoomLevels.initialMax] as [number, number],
            'reset'
          )
        }}
        showResetButton={Boolean(ticksAtLimit[Bound.LOWER] || ticksAtLimit[Bound.UPPER])}
        zoomLevels={zoomLevels}
        resetKey={resetKey}
      />
      <ChartHeader aria-live="polite">
        <HeaderPrice>
          {hoveredPoint
            ? `≈ ${hoveredPoint.price.toLocaleString(undefined, { maximumSignificantDigits: 7 })}`
            : 'Price history unavailable'}
        </HeaderPrice>
        {priceChange !== undefined && (
          <HeaderMeta style={{ color: priceChange >= 0 ? theme.accentSuccess : theme.accentFailure }}>
            {priceChange >= 0 ? '+' : ''}
            {priceChange.toFixed(2)}%
          </HeaderMeta>
        )}
        {hoveredPoint && (
          <HeaderMeta>
            {hoveredPoint.timestamp.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
          </HeaderMeta>
        )}
      </ChartHeader>
      <svg
        ref={zoomRef}
        width="100%"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', height: 'auto', overflow: 'visible', touchAction: 'manipulation' }}
        aria-label="Liquidity range chart"
        onMouseMove={onMouseMove}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <title>Liquidity range chart</title>
        <defs>
          <clipPath id={`${id}-chart-clip`}>
            <rect x="0" y="0" width={innerWidth} height={innerHeight} />
          </clipPath>
          <clipPath id={`${id}-price-clip`}>
            <rect x="0" y="0" width={mainPlotWidth} height={innerHeight} />
          </clipPath>
          <clipPath id={`${id}-price-upper-range-clip`}>
            <rect x="0" y={visibleRangeTop} width={mainPlotWidth} height={upperRangeHeight} />
          </clipPath>
          <clipPath id={`${id}-price-lower-range-clip`}>
            <rect x="0" y={visibleCurrentY} width={mainPlotWidth} height={lowerRangeHeight} />
          </clipPath>
          <linearGradient id={`${id}-current-price-dot`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={styles.brush.handle.east} />
            <stop offset="100%" stopColor={styles.brush.handle.west} />
          </linearGradient>
        </defs>

        <g transform={`translate(${margins.left},${margins.top})`}>
          <g clipPath={`url(#${id}-chart-clip)`}>
            {upperRangeHeight > 0 && (
              <rect
                x="0"
                y={visibleRangeTop}
                width={rangeWidth}
                height={upperRangeHeight}
                fill={styles.brush.handle.east}
                opacity="0.14"
                pointerEvents="none"
              />
            )}
            {lowerRangeHeight > 0 && (
              <rect
                x="0"
                y={visibleCurrentY}
                width={rangeWidth}
                height={lowerRangeHeight}
                fill={styles.brush.handle.west}
                opacity="0.14"
                pointerEvents="none"
              />
            )}

            <LiquidityBars
              series={series}
              yScale={yScale}
              current={current}
              brushDomain={brushDomain}
              innerWidth={innerWidth}
              innerHeight={innerHeight}
              railWidth={LIQUIDITY_RAIL_WIDTH}
              lowerColor={styles.brush.handle.west}
              upperColor={styles.brush.handle.east}
            />

            {history.length > 1 && (
              <g pointerEvents="none" clipPath={`url(#${id}-price-clip)`}>
                <PriceHistory points={history} xScale={timeScale} yScale={yScale} stroke={theme.textSecondary} />
                <PriceHistory
                  points={history}
                  xScale={timeScale}
                  yScale={yScale}
                  stroke={styles.brush.handle.east}
                  clipPath={`url(#${id}-price-upper-range-clip)`}
                />
                <PriceHistory
                  points={history}
                  xScale={timeScale}
                  yScale={yScale}
                  stroke={styles.brush.handle.west}
                  clipPath={`url(#${id}-price-lower-range-clip)`}
                />
              </g>
            )}

            <Line value={current} yScale={yScale} innerWidth={rangeWidth} innerHeight={innerHeight} />
            <circle
              cx={mainPlotWidth}
              cy={yScale(current)}
              r="4"
              fill={`url(#${id}-current-price-dot)`}
              pointerEvents="none"
            />

            {hoveredPoint && hoveredIndex !== null && (
              <g pointerEvents="none" clipPath={`url(#${id}-price-clip)`}>
                <HoverLine
                  x1={timeScale(hoveredPoint.timestamp)}
                  y1="0"
                  x2={timeScale(hoveredPoint.timestamp)}
                  y2={innerHeight}
                />
                <HoverPoint cx={timeScale(hoveredPoint.timestamp)} cy={yScale(hoveredPoint.price)} r="4" />
              </g>
            )}
          </g>

          {history.length > 1 && <PriceHistoryAxis xScale={timeScale} innerHeight={innerHeight} />}

          <VerticalBrush
            id={id}
            yScale={yScale}
            interactive={interactive}
            brushExtent={brushDomain ?? (yScale.domain() as [number, number])}
            innerWidth={innerWidth}
            innerHeight={innerHeight}
            setBrushExtent={onBrushDomainChange}
            lowerHandleColor={styles.brush.handle.west}
            upperHandleColor={styles.brush.handle.east}
            brushLabels={brushLabels}
          />
        </g>
      </svg>
    </>
  )
}
